/**
 * The Git tab's calls (docs/DESIGN-GIT-TAB.md): every service of a project is its own git repo (`services/<name>`),
 * reached through vfs-service's project routes (`<vfsServiceUrl>/api/v1/projects/:id/git/...`, the same base, sign-in
 * and ACL headers as the Configuration and Data tabs), each call scoped to one `service`:
 * - `GET /git/overview` -> `{ repos: [{ service, branch, branches, changed, staged, untracked, ahead, behind, upstream,
 *   lastCommit: { sha, subject, date, author } | null, tags, remotes: [{ name, url, pushUrl?, webUrl, host }] }], flow: { cloneBase } }`;
 *   also `initialized`, `conflicted`; `ahead`/`behind` null without an upstream; `remotes` never lists `flow`
 * - `POST /git/commit { service, message }` -> `{ shas: [{ sha, repo }] }` (that repo only, everything staged)
 * - `GET /git/tags?service=`, `POST /git/tag { service, name, message?, sha? }`, `DELETE /git/tag/:name?service=`
 * - `POST /git/push { service, remote, branch?, tags?, tag? }`, `POST /git/pull|fetch { service, remote, branch? }`;
 *   `fetch` with `tags: true` also brings in every tag and drops branches deleted on the remote (never a local-only
 *   tag); `POST /git/remote-tags { service, remote }` -> `{ remote, tags: [{ name, sha }] }` (what the remote has);
 *   "All remotes" is one call per remote, here (pushAll / pullAll / fetchAll)
 * - `GET /git/branches?service=`, `POST /git/branch { service, name, from? }`, `POST /git/checkout { service, branch | tag }` (branch
 *   `<remote>/<name>`: a local tracking branch; tag: detached HEAD)
 * - `GET /git/remote?service=`, `POST /git/remote { service, name, url, account? }`, `DELETE /git/remote/:name?service=`,
 *   `POST /git/remote-url { service, name, url }`
 * - the person's clone token for "Clone from Flow": `POST <vfsServiceUrl>/api/v1/me/git-token { projectId, mode, ttlDays }`
 *   -> `{ token, expiresAt, mode }` (the sandbox gateway; shown once, never kept here; 403 no_access / read_only /
 *   tab_hidden, 429 too many).
 * Errors are GitError with the server's `{ error, code, connectUrl? }`; `needsAccount(error)`: connect an account.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';

export class GitError extends Error {
  constructor(message, status, code = null, connectUrl = null) {
    super(message);
    this.name = 'GitError';
    this.status = status;
    this.code = code;
    this.connectUrl = connectUrl;
  }
}

/** Where a person connects a git account (Features › Connections), when the server gives no `connectUrl`. */
export const CONNECTIONS_PATH = '/features/connections';

const ACCOUNT_CODES = new Set(['no_account', 'account_refused', 'accounts_off', 'no_person', 'account_expired', 'account_missing']);

/** Whether the error is about the person's git account: the place offers "Connect account". */
export const needsAccount = (error) => Boolean(error?.code) && (ACCOUNT_CODES.has(error.code) || /account/i.test(error.code));

/** Where "Connect account" goes. */
export const connectHref = (error) => error?.connectUrl || CONNECTIONS_PATH;

const base = () => String(config.vfsServiceUrl || '').replace(/\/+$/, '');
const seg = (value) => encodeURIComponent(String(value));
const projectUrl = (projectId, path) => `${base()}/api/v1/projects/${seg(projectId)}${path}`;
const forService = (service) => `?service=${seg(service)}`;

async function call(url, { method = 'GET', body } = {}) {
  if (!base()) { throw new GitError('vfs-service is not configured.', 0, 'not_configured'); }
  const json = body !== undefined;
  let response;
  try {
    response = await getAuthorizedFetch()(url, {
      method,
      headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
      ...(json ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    throw new GitError(`Your sandbox could not be reached (${error.message}).`, 0, 'unreachable');
  }
  const data = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 404 && !data) {
      throw new GitError('Your sandbox runs an older version: restart it to use Git here.', 404, 'sandbox_outdated');
    }
    throw new GitError(data?.error || data?.message || `Request failed (${response.status})`, response.status, data?.code || null, data?.connectUrl || null);
  }
  return data || {};
}

const post = (projectId, path, body) => call(projectUrl(projectId, path), { method: 'POST', body });
const clean = (fields) => Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined && value !== null && value !== ''));

/** Every service's repo at a glance, and Flow's own clone base. */
export async function getOverview(projectId) {
  const data = await call(projectUrl(projectId, '/git/overview'));
  return { repos: Array.isArray(data.repos) ? data.repos : [], flow: data.flow || {} };
}

/** Commits one service's repo (everything staged): `{ shas }`. */
export const commit = (projectId, { service, message }) => post(projectId, '/git/commit', { service, message });

export const listTags = (projectId, service) => call(projectUrl(projectId, `/git/tags${forService(service)}`));

/** A lightweight tag, or an annotated one with `message`; at HEAD unless `sha`. */
export const createTag = (projectId, { service, name, message, sha }) => post(projectId, '/git/tag', clean({ service, name, message, sha }));

export const deleteTag = (projectId, service, name) => call(projectUrl(projectId, `/git/tag/${seg(name)}${forService(service)}`), { method: 'DELETE' });

/** `tags: true` pushes every tag too; `tag` one tag only. */
export const push = (projectId, { service, remote, branch, tags, tag }) => post(projectId, '/git/push', clean({ service, remote, branch, tags: tags ? true : undefined, tag }));

export const pull = (projectId, { service, remote, branch }) => post(projectId, '/git/pull', clean({ service, remote, branch }));

/** `tags: true`: the remote's branches (pruned) and every tag. */
export const fetchRemote = (projectId, { service, remote, branch, tags }) => post(projectId, '/git/fetch', clean({ service, remote, branch, tags: tags ? true : undefined }));

/** The tags a remote has: `{ remote, tags: [{ name, sha }] }`. */
export const remoteTags = (projectId, { service, remote }) => post(projectId, '/git/remote-tags', clean({ service, remote }));

export const listBranches = (projectId, service) => call(projectUrl(projectId, `/git/branches${forService(service)}`));

/** A new branch (from `from`, else HEAD), checked out. */
export const createBranch = (projectId, { service, name, from }) => post(projectId, '/git/branch', clean({ service, name, from }));

/** A local branch, a remote one (`origin/name`: a tracking branch is made) or `tag` (detached). */
export const checkout = (projectId, { service, branch, tag }) => post(projectId, '/git/checkout', clean({ service, branch, tag }));

export const listRemotes = (projectId, service) => call(projectUrl(projectId, `/git/remote${forService(service)}`));

/** `account`: the connected account to use with it (the person's default for the host when left out). */
export const addRemote = (projectId, { service, name, url, account }) => post(projectId, '/git/remote', clean({ service, name, url, account }));

export const removeRemote = (projectId, service, name) => call(projectUrl(projectId, `/git/remote/${seg(name)}${forService(service)}`), { method: 'DELETE' });

export const setRemoteUrl = (projectId, { service, name, url }) => post(projectId, '/git/remote-url', { service, name, url });

/**
 * One call per remote, in order, each with its own answer: `[{ remote, ok, result?, error? }]`. One that fails does not
 * stop the others ("All remotes").
 */
async function eachRemote(remotes, run) {
  const out = [];
  for (const remote of remotes) {
    try {
      // eslint-disable-next-line no-await-in-loop -- one remote at a time: the repo is the same, and the order shows
      out.push({ remote, ok: true, result: await run(remote) });
    } catch (error) {
      out.push({ remote, ok: false, error });
    }
  }
  return out;
}

export const pushAll = (projectId, { service, remotes, branch, tags, tag }) => eachRemote(remotes, (remote) => push(projectId, { service, remote, branch, tags, tag }));
export const fetchAll = (projectId, { service, remotes, tags = true }) => eachRemote(remotes, (remote) => fetchRemote(projectId, { service, remote, tags }));
export const pullAll = (projectId, { service, remotes, branch }) => eachRemote(remotes, (remote) => pull(projectId, { service, remote, branch }));

/** A token to clone (and, `mode: 'write'`, push to) this project from Flow: `{ token, expiresAt }`, shown once. */
export function createGitToken({ projectId, mode = 'read', ttlDays = 30 }) {
  return call(`${base()}/api/v1/me/git-token`, { method: 'POST', body: { projectId, mode: mode === 'write' ? 'write' : 'read', ttlDays } });
}
